import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeaderSection from '../../components/home/HeaderSection';
import FooterSection from '../../components/home/FooterSection';
import { ROUTES } from '../../constants/routes';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { getUserSubscriptions } from '../../services/payments';
import {
  clearPendingPayment,
  readPendingPayment,
  savePendingPayment,
} from '../../utils/pendingPayment';

const POLL_MS = 2500;
const MAX_WAIT_MS = 12 * 60 * 1000;

/** Same rule as home access: must be active, not just status=completed. */
function isFullyActiveSubscription(sub, subscriptionId) {
  if (!sub || String(sub.id) !== String(subscriptionId)) return false;
  return Boolean(
    sub.active === true && (sub.status === 'completed' || sub.is_subscription === true)
  );
}

function isPaymentMarkedCompleted(sub, subscriptionId) {
  if (!sub || String(sub.id) !== String(subscriptionId)) return false;
  const status = String(sub.status || '').toLowerCase();
  return status === 'completed' || status === 'paid';
}

export default function PaymentPendingPage() {
  const navigate = useNavigate();
  const { refetch } = useSubscriptionContext();
  const [pending, setPending] = useState(() => readPendingPayment());
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('Waiting for payment confirmation…');
  const startedAtRef = useRef(Date.now());
  const handledRef = useRef(false);

  const goSuccess = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const current = readPendingPayment() || pending || {};
    savePendingPayment(current);
    refetch?.();
    navigate(ROUTES.PAYMENT_SUCCESS, { replace: true });
  }, [navigate, pending, refetch]);

  const goFailed = useCallback(
    (errorMessage) => {
      if (handledRef.current) return;
      handledRef.current = true;
      savePendingPayment({
        ...(readPendingPayment() || pending || {}),
        errorMessage,
      });
      navigate(
        {
          pathname: ROUTES.PAYMENT_FAILED,
          search: errorMessage ? `?error=${encodeURIComponent(errorMessage)}` : '',
        },
        { replace: true }
      );
    },
    [navigate, pending]
  );

  const checkPaymentStatus = useCallback(async () => {
    const current = readPendingPayment() || pending;
    const subscriptionId = current?.subscriptionId;
    if (!subscriptionId) {
      goFailed('Payment session expired. Please try again.');
      return false;
    }

    setChecking(true);
    try {
      const res = await getUserSubscriptions();
      const subscriptions = res?.data?.subscriptions || [];
      const match = subscriptions.find((sub) => String(sub.id) === String(subscriptionId));

      if (match && isFullyActiveSubscription(match, subscriptionId)) {
        goSuccess();
        return true;
      }

      if (match && isPaymentMarkedCompleted(match, subscriptionId)) {
        setMessage(
          'Payment received, but subscription is not active yet. Waiting for verification…'
        );
        return false;
      }

      setMessage('Payment not confirmed yet. Complete payment in the Razorpay window.');
      return false;
    } catch {
      setMessage('Could not verify payment yet. Retrying…');
      return false;
    } finally {
      setChecking(false);
    }
  }, [goFailed, goSuccess, pending]);

  useEffect(() => {
    const current = readPendingPayment();
    if (!current?.subscriptionId) {
      navigate(ROUTES.PRICING, { replace: true });
      return undefined;
    }

    setPending(current);
    startedAtRef.current = Date.now();

    // Open Razorpay if popup was blocked earlier or user refreshed this page.
    if (current.paymentUrl && !current.paymentWindowOpened) {
      const popup = window.open(current.paymentUrl, 'chse_razorpay_payment');
      savePendingPayment({
        ...current,
        paymentWindowOpened: Boolean(popup),
      });
      setPending((prev) => ({ ...prev, paymentWindowOpened: Boolean(popup) }));
    }

    const poll = window.setInterval(async () => {
      if (handledRef.current) return;
      if (Date.now() - startedAtRef.current > MAX_WAIT_MS) {
        window.clearInterval(poll);
        setMessage('Still waiting. If you already paid, tap “I’ve paid”.');
        return;
      }
      await checkPaymentStatus();
    }, POLL_MS);

    checkPaymentStatus();

    return () => window.clearInterval(poll);
  }, [checkPaymentStatus, navigate]);

  const openPaymentWindow = () => {
    const current = readPendingPayment() || pending;
    if (!current?.paymentUrl) return;
    const popup = window.open(current.paymentUrl, 'chse_razorpay_payment');
    const next = { ...current, paymentWindowOpened: Boolean(popup) };
    savePendingPayment(next);
    setPending(next);
    if (!popup) {
      setMessage('Popup blocked. Allow popups, or open the payment link below.');
    }
  };

  const cancelPayment = () => {
    clearPendingPayment();
    navigate(ROUTES.PRICING, { replace: true });
  };

  return (
    <>
      <HeaderSection />
      <main className="bg-[#F5F8FC] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-lg rounded-2xl bg-white px-6 py-10 text-center shadow-[0_8px_40px_rgba(0,0,0,0.08)] sm:px-10">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F4FC]">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1A87CC] border-t-transparent" />
          </div>

          <h1 className="mt-6 text-2xl font-bold text-[#1B1B1B] sm:text-[28px]">
            Complete Your Payment
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#666666] sm:text-base">{message}</p>

          {(pending?.planName || pending?.amount) && (
            <div className="mt-6 rounded-xl bg-[#F5F8FC] px-4 py-3 text-left text-sm text-[#1B1B1B]">
              {pending.planName ? (
                <p>
                  <span className="text-[#999999]">Plan: </span>
                  {pending.planName}
                </p>
              ) : null}
              {pending.amount != null ? (
                <p className="mt-1">
                  <span className="text-[#999999]">Amount: </span>
                  ₹{pending.amount}
                </p>
              ) : null}
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3">
            <button
              type="button"
              onClick={openPaymentWindow}
              className="rounded-lg bg-[#1A87CC] px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Open Payment Page
            </button>
            <button
              type="button"
              onClick={checkPaymentStatus}
              disabled={checking}
              className="rounded-lg border border-[#1A87CC] px-6 py-3 text-sm font-semibold text-[#1A87CC] transition hover:bg-[#1A87CC]/5 disabled:opacity-60"
            >
              {checking ? 'Checking…' : 'I’ve paid'}
            </button>
            <button
              type="button"
              onClick={cancelPayment}
              className="text-sm font-medium text-[#999999] transition hover:text-[#1B1B1B]"
            >
              Cancel
            </button>
            {pending?.paymentUrl ? (
              <a
                href={pending.paymentUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-[#1A87CC] underline"
              >
                Open payment link in new tab
              </a>
            ) : null}
            <Link to={ROUTES.HOME} className="text-sm text-[#999999] hover:text-[#1B1B1B]">
              Go to Home
            </Link>
          </div>
        </div>
      </main>
      <FooterSection />
    </>
  );
}
